import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { useTranslation } from "react-i18next";
import DialogForm from "./DialogForm";
import { Category, MenuItem } from "@/model/entities";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectContent,
} from "./ui/select";

export type MenuItemFormData = {
  id?: string;
  name: string;
  description?: string;
  price: number;
  image?: FileList | string | null;
  isAvailable?: boolean;
  categoryId: string;
  order?: number;
};

export type EnhancedMenuItemDialogFormProps = {
  open?: boolean;
  mode: "add" | "edit";
  defaultValues?: Partial<MenuItem>;
  categories: Category[];
  onAdd?: (data: MenuItemFormData) => void;
  onUpdate?: (id: string, data: MenuItemFormData) => void;
  onCancel?: () => void;
};

// Keep schema creation inside component so we can use translations for messages

export const EnhancedMenuItemDialogFormStateless: React.FC<
  EnhancedMenuItemDialogFormProps
> = ({
  open = true,
  mode,
  defaultValues = {},
  categories,
  onAdd,
  onUpdate,
  onCancel,
}) => {
  const { t } = useTranslation();

  console.log("default values:  ", defaultValues);

  // Remove debug logging (causes duplicate prints in StrictMode/dev)

  // Validation schema: minimal and reusable. Keep component stateless - only validation + submit.
  const schema = yup.object({
    id: yup.string().optional(),
    name: yup.string().required(t("input.required")).max(200),
    description: yup.string().optional().max(1000),
    price: yup
      .number()
      .typeError(t("input.positiveNumber"))
      .required(t("input.required"))
      .min(0, t("input.positiveNumber")),
    image: yup.mixed<FileList | string>().nullable().optional(),
    isAvailable: yup.boolean().optional(),
    categoryId: yup.string().required(t("menuItemForm.categoryRequired")),
  });
  const { register, handleSubmit, formState, watch, setValue, control, reset } =
    useForm<MenuItemFormData>({
      resolver: yupResolver(schema),
      defaultValues: {
        name: defaultValues.name ?? "",
        description: defaultValues.description ?? "",
        price:
          defaultValues.price !== undefined ? Number(defaultValues.price) : 0,
        image: defaultValues.imageUrl ?? null,
        isAvailable: defaultValues.isAvailable ?? true,
        id: defaultValues.id,
        categoryId: defaultValues.categoryId,
        order: defaultValues.order,
      },
    });

  const { errors, isSubmitting } = formState;
  const watchedImage = watch("image");

  const [previewUrl, setPreviewUrl] = useState<string | null>(
    typeof defaultValues.imageUrl === "string" ? defaultValues.imageUrl : null,
  );

  // Reset form values when opening the dialog or when meaningful default values change.
  // Use specific fields in the dependency array instead of the whole object to avoid
  // repeatedly triggering reset if the parent passes a new object reference each render.
  useEffect(() => {
    if (!open) return;

    reset({
      name: defaultValues.name ?? "",
      description: defaultValues.description ?? "",
      price:
        defaultValues.price !== undefined ? Number(defaultValues.price) : 0,
      image: defaultValues.imageUrl ?? null,
      isAvailable: defaultValues.isAvailable ?? true,
      id: defaultValues.id,
      categoryId: defaultValues.categoryId,
    });

    // also update previewUrl immediately when reset happens
    setPreviewUrl(
      typeof defaultValues.imageUrl === "string"
        ? defaultValues.imageUrl
        : null,
    );
    // Only watch the meaningful parts of defaultValues to avoid infinite updates
  }, [
    open,
    defaultValues?.id,
    defaultValues?.name,
    defaultValues?.price,
    defaultValues?.description,
    defaultValues?.imageUrl,
    defaultValues?.categoryId,
    defaultValues?.isAvailable,
    reset,
  ]);

  // Update previewUrl when the watched image changes. Handle FileList and string values.
  useEffect(() => {
    let createdUrl: string | null = null;

    if (!watchedImage) {
      setPreviewUrl(
        typeof defaultValues.imageUrl === "string"
          ? defaultValues.imageUrl
          : null,
      );
      return () => {
        if (createdUrl) URL.revokeObjectURL(createdUrl);
      };
    }

    // If FileList, create object URL from first file
    if (watchedImage instanceof FileList && watchedImage.length > 0) {
      createdUrl = URL.createObjectURL(watchedImage[0]);
      setPreviewUrl(createdUrl);
      return () => {
        if (createdUrl) URL.revokeObjectURL(createdUrl);
      };
    }

    // If it's a string (existing image URL), use it directly
    if (typeof watchedImage === "string") {
      setPreviewUrl(watchedImage || null);
      return () => {
        if (createdUrl) URL.revokeObjectURL(createdUrl);
      };
    }

    setPreviewUrl(null);
    return () => {
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [watchedImage, defaultValues.imageUrl, setPreviewUrl]);

  const onSubmit = async (data: MenuItemFormData) => {
    // Ensure price is a number when passed out
    const normalized: MenuItemFormData = {
      ...data,
      price:
        typeof data.price === "string" && data.price !== ""
          ? Number(data.price)
          : (data.price as number),
    };

    try {
      if (mode === "add") {
        console.log("Submitting new item:", normalized);
        // Support sync or promise-based handlers
        await Promise.resolve(onAdd?.(normalized));
        // Close dialog after successful add
        onCancel?.();
      } else if (defaultValues.id) {
        console.log("Submitting updated item:", defaultValues.id, normalized);
        await Promise.resolve(onUpdate?.(defaultValues.id, normalized));
        // Close dialog after successful update
        onCancel?.();
      }
    } catch (err) {
      // Let the consumer handle errors if they rethrow; log for debugging
      console.error("Error submitting menu item:", err);
      // do not close dialog on error
      throw err;
    }
  };

  if (!open) return null;

  // This component intentionally contains no business logic beyond validation and submit.
  // Render inside the project's DialogForm for consistent dialog UI and footer handling.
  const title =
    mode === "add"
      ? t("menuItemForm.addMenuItem")
      : t("menuItemForm.editMenuItem");

  const footer = (
    <div className="flex gap-2">
      <Button
        type="button"
        variant="outline"
        onClick={onCancel}
        disabled={isSubmitting}
      >
        {t("common.cancel")}
      </Button>
      <Button
        type="submit"
        onClick={handleSubmit(onSubmit)}
        disabled={isSubmitting}
      >
        {mode === "add" ? t("buttons.add") : t("buttons.edit")}
      </Button>
    </div>
  );

  return (
    <DialogForm
      open={open}
      title={title}
      onClose={() => onCancel?.()}
      size="md"
      footer={footer}
    >
      <div className="p-4">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="categoryId">{t("menuItemForm.category")}</Label>
            <Controller
              control={control}
              name="categoryId"
              render={({ field }) => (
                <Select
                  value={field.value ?? ""}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("menuItemForm.selectCategory")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id ?? ""} value={cat.id ?? ""}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.categoryId && (
              <p className="text-red-500 text-sm">
                {errors.categoryId.message}
              </p>
            )}
          </div>
          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="name">{t("menuItemForm.itemName")}</Label>
            <Input id="name" {...register("name")} />
            {errors.name && (
              <p className="text-red-500 text-sm">{errors.name.message}</p>
            )}
          </div>

          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="description">
              {t("menuItemForm.itemDescription")}
            </Label>
            <Textarea id="description" {...register("description")} />
            {errors.description && (
              <p className="text-red-500 text-sm">
                {errors.description.message}
              </p>
            )}
          </div>

          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="price">{t("menuItemForm.itemPrice")}</Label>
            <Input
              id="price"
              type="number"
              step="0.01"
              {...register("price")}
            />
            {errors.price && (
              <p className="text-red-500 text-sm">{errors.price.message}</p>
            )}
          </div>

          <div className="grid w-full items-center gap-1.5">
            <Label htmlFor="image">{t("menuItemForm.itemImage")}</Label>
            <div className="flex items-center gap-2">
              <Input
                type="file"
                id="image"
                accept="image/*"
                {...register("image")}
              />
              <Button
                type="button"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  // Clear react-hook-form value and reset the DOM input value
                  setValue("image", null);
                  const input = document.getElementById(
                    "image",
                  ) as HTMLInputElement | null;
                  if (input) input.value = "";
                }}
              >
                {t("clear")}
              </Button>
            </div>
            {errors.image && (
              <p className="text-red-500 text-sm">
                {typeof errors.image.message === "string"
                  ? errors.image.message
                  : ""}
              </p>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Controller
              control={control}
              name="isAvailable"
              render={({ field }) => (
                <Checkbox
                  id="isAvailable"
                  checked={
                    typeof field.value === "boolean" ? field.value : true
                  }
                  onCheckedChange={(val) => field.onChange(val)}
                />
              )}
            />
            <Label htmlFor="isAvailable">{t("menuItemForm.isAvailable")}</Label>
          </div>
        </form>

        {/* add preview image section */}
        <div className="mt-4">
          {previewUrl && (
            <img
              src={previewUrl}
              alt={t("menuItemForm.imagePreviewAlt") ?? "Preview"}
              className="mt-2 max-w-xs rounded-md border"
            />
          )}
        </div>
      </div>
    </DialogForm>
  );
};

export default EnhancedMenuItemDialogFormStateless;
